// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useContext } from "react";
import { css } from "styled-components";

// XNG-Changes
const enum fields {
    showBrandLogo = "showBrandLogo",
    showBrandName = "showBrandName",
}
interface Props extends WidgetProps {
    // XNG-Changes
    fields: {
        [fields.showBrandLogo]: boolean;
        [fields.showBrandName]: boolean;
    };
}

export interface BrandDetailsLogoStyles {
    container?: InjectableCss;
    image?: LazyImageProps;
    heading?: TypographyProps;
}

export const logoStyles: BrandDetailsLogoStyles = {
    heading: {
        variant: "h1",
        css: css`
            overflow-wrap: break-word;
            word-wrap: break-word;
        `,
    },
    container: {
        css: css`
            width: 100%;
            margin: 15px;
        `,
    },
    image: {
        css: css`
            width: 100%;
            img {
                height: 100%;
            }
        `,
    },
};

const styles = logoStyles;

// XNG-Changes
const BrandDetailsLogo: FC<Props> = ({ fields: { showBrandLogo, showBrandName } }) => {
    const { value: brand } = useContext(BrandStateContext);
    if (!brand) {
        return null;
    }
    return (
        // XNG-Changes
        <StyledWrapper {...styles.container} data-test-selector="brandLogo">
            {showBrandLogo && brand.logoLargeImagePath && (
                <LazyImage src={brand.logoLargeImagePath} altText={brand.logoAltText} {...styles.image} />
            )}
            {(showBrandName || (showBrandLogo && !brand.logoLargeImagePath)) && (
                <Typography data-test-selector="brandLogoHeading" {...styles.heading}>
                    {brand.name}
                </Typography>
            )}
            {/* {brand.logoLargeImagePath ? (
                brand.logoLargeImagePath && (
                    <LazyImage src={brand.logoLargeImagePath} altText={brand.logoAltText} {...styles.image} />
                )
            ) : (
                <Typography data-test-selector="brandLogoHeading" {...styles.heading}>
                    {brand.name}
                </Typography>
            )} */}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: BrandDetailsLogo,
    definition: {
        group: "Brand Details",
        icon: "diamond",
        displayName: "Brand Logo",
        allowedContexts: ["BrandDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.showBrandLogo,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showBrandName,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
